import { Spinner } from "@blueprintjs/core"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import Divider from "components/Atoms/Divider"
import Paper from "components/Atoms/Paper"
import DynamicSelector from "components/Molecules/DynamicSelector"
import NoResults from "components/Organisms/NoResults"
import { useAllUsersByGroupSelectorLazyQuery } from "generated/graphql"
import { ALL_SURVEY_AREAS } from "graphql/survey-areas/survey-areas.queries.gql"
import React, { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useCurrentDcId } from "store/auth.redux"
import clsx from "utils/clsx"
import useSalesManColorMap, { SALESMEN_COLORS } from "./salesman-colours.store"

const SalesManList = () => {
  const currentDcId = useCurrentDcId()
  const [params, setParams] = useSearchParams()
  const surveyAreaId = parseInt(params.get("sid"))
  const surveyAreaName = params.get("sname")
  const salesman = params.getAll("salesman")
  const setColorMap = useSalesManColorMap(store => store.setColorMap)

  const [fetch, { data, loading }] = useAllUsersByGroupSelectorLazyQuery({
    onCompleted({ usersByGroup }) {
      if (!salesman.length && surveyAreaId && surveyAreaName) {
        params.delete("salesman")
        for (const user of usersByGroup) {
          params.append("salesman", user.id.toString())
        }
        setParams(params)
      }
    },
  })

  useEffect(() => {
    if (surveyAreaId) {
      fetch({
        variables: {
          group: "Salesman",
          filter: {
            surveyAreaId: surveyAreaId as any,
          },
        },
      })
    }
  }, [fetch, surveyAreaId])

  const salesMenList = data?.usersByGroup?.map((user, idx) => ({
    ...user,
    color: SALESMEN_COLORS[idx],
  }))

  function setSurveyArea(area: any) {
    if (!area?.id) return
    params.delete("salesman")
    params.set("sid", area?.id.toString())
    params.set("sname", area.properties.name)
    setParams(params)
  }
  useEffect(() => {
    if (salesMenList) {
      setColorMap(salesMenList)
    }
  }, [salesMenList, setColorMap])

  return (
    <Paper className="w-1/5 xl:w-1/6 p-3 h-full flex flex-col flex-nowrap">
      <DynamicSelector
        onCompleted={({ allSurveyAreas }) => {
          const first = allSurveyAreas[0]
          const isValid = allSurveyAreas.find(area => area.id === surveyAreaId)
          if (!surveyAreaId || !isValid) {
            setSurveyArea(first)
          }
        }}
        path="surveyArea"
        className="mb-0"
        name="properties.name"
        onChange={(_, value) => {
          setSurveyArea(value)
        }}
        query={ALL_SURVEY_AREAS}
        variables={{
          filter: {
            distributionCentreIds: [currentDcId],
          },
        }}
        renderText={() =>
          surveyAreaId ? surveyAreaName : "Select Survey Area"
        }
      />
      <Divider className="my-2" />
      <p>Salesmen assigned to the above survey area</p>
      {loading ? (
        <Spinner />
      ) : salesMenList ? (
        <div className="grow min-h-[2em] overflow-auto">
          {salesMenList.map((user, idx) => {
            const isSelected = salesman?.includes?.(user.id)
            const color = user.color

            const handleClick = e => {
              e.preventDefault()
              const newSalesmen = isSelected
                ? salesman?.filter(salesman => salesman !== user.id)
                : [...salesman, user.id]

              params.delete("salesman")
              for (const salesman of newSalesmen) {
                params.append("salesman", salesman.toString())
              }
              setParams(params)
            }
            return (
              <div
                tabIndex={0}
                aria-checked={isSelected}
                role={"checkbox"}
                onClick={handleClick}
                className={clsx(
                  "rounded-xl p-3 flex items-center filter hover:opacity-80 cursor-pointer",
                  idx !== salesMenList.length - 1 && "mb-3",
                  isSelected
                    ? `bg-${color}-100 text-${color}-600`
                    : "bg-gray-400 text-gray-600 "
                )}
                key={user.id}
              >
                <CustomCheckbox
                  className="mb-0"
                  // size="sm"
                  checked={isSelected}
                  // labelClassName="h-4"
                  // checkMarkClassName={isSelected && `bg-${color}-600`}
                />
                <span>{user.fullName}</span>
              </div>
            )
          })}
        </div>
      ) : (
        <NoResults
          title="Unable to find any salesman"
          icon="search"
          description={"Try selecting a survey area from the dropdown above"}
        />
      )}
    </Paper>
  )
}

export default SalesManList
